import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { LoginDialog } from './LoginDialog';
import {
  LoginPromptContext,
  type OpenLoginOptions,
} from './loginPromptContext';

/**
 * One login dialog for the whole app: every "you need to log in for this"
 * on a page opens the same panel as /login, and the visitor stays on the page. Mount once, above
 * anything that asks for login.
 */
export function LoginPromptProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const callbacks = useRef<Pick<OpenLoginOptions, 'onDismiss' | 'onLoggedIn'>>(
    {},
  );

  const openLogin = useCallback(
    (options: OpenLoginOptions = {}) => {
      callbacks.current = {
        onDismiss: options.onDismiss,
        onLoggedIn: options.onLoggedIn,
      };
      setReturnTo(options.returnTo ?? location.pathname + location.search);
      setOpen(true);
    },
    [location.pathname, location.search],
  );

  const dismiss = useCallback(() => {
    setOpen(false);
    callbacks.current.onDismiss?.();
    callbacks.current = {};
  }, []);

  const loggedIn = useCallback(() => {
    setOpen(false);
    callbacks.current.onLoggedIn?.();
    callbacks.current = {};
  }, []);

  const value = useMemo(() => ({ openLogin }), [openLogin]);

  return (
    <LoginPromptContext.Provider value={value}>
      {children}
      <LoginDialog
        open={open}
        returnTo={returnTo}
        onDismiss={dismiss}
        onLoggedIn={loggedIn}
      />
    </LoginPromptContext.Provider>
  );
}
